import { DragulaContainer } from '@beesoft/react-dragula';
import type { Signal } from '@preact/signals-react';
import clsx from 'clsx';
import type { Key } from 'react';
import { useCallback } from 'react';
import type { OdinDataRetrieval } from '../../common/data-retrieval/data-retrieval-interfaces';
import type {
  ContinuousScrollerFilter,
  ContinuousScrollerSort,
} from '../continuous-scroller/continuous-scroller-types';
import DataGridColumn from './data-grid-column.component';
import type { DataGridColumnSettings, DataGridHeaderRowIndicator } from './data-grid-settings.interface';
import { OverlayScrollDetectionType } from '../common/overlay-panel/overlay-panel.props';
import { reorderSignalColumns } from './functions/data-grid-signal-functions';
import { SignalFilter } from '../signals/filter/signal-filter.component';

export interface DataGridHeaderProps {
  columns: Signal<Array<DataGridColumnSettings>>;
  rowWidth: Signal<number>;
  indicatorRowWidth: Signal<number>;
  availableWidth: Signal<number>;
  search?: OdinDataRetrieval;
  filterSignal: Signal<Array<ContinuousScrollerFilter>>;
  sortSignal: Signal<Array<ContinuousScrollerSort>>;
  scrollDetection?: OverlayScrollDetectionType;
  useIndicatorColumn: boolean;
  onColumnDragStart?: (column: DataGridColumnSettings) => void;
  onColumnDragEnd?: (column: DataGridColumnSettings) => void;
  headerRowIndicator?: DataGridHeaderRowIndicator;
  forceResetColumnMap: Map<Key, boolean>;
}

export default function DataGridHeader({
  columns,
  rowWidth,
  indicatorRowWidth,
  availableWidth,
  search,
  filterSignal,
  sortSignal,
  scrollDetection = OverlayScrollDetectionType.Parent,
  useIndicatorColumn,
  onColumnDragStart,
  onColumnDragEnd,
  headerRowIndicator,
  forceResetColumnMap,
}: DataGridHeaderProps) {
  const onItemsChanged = useCallback((items: Array<unknown>) => {
    const convertedColumns = items as Array<DataGridColumnSettings>;
    const columnIds = convertedColumns.map((column) => column.id);

    reorderSignalColumns(columns, columnIds);
  }, []);

  const onIndicatorColumnCreated = useCallback((element: HTMLElement) => {
    if (indicatorRowWidth.value !== element.offsetWidth) {
      indicatorRowWidth.value = element.offsetWidth;
    }
  }, []);

  const indicatorClassNames = clsx(
    'odin-bg-mono-1 odin-flex odin-items-center odin-justify-center odin-w-10 odin-h-full odin-overflow-hidden',
    headerRowIndicator?.indicatorClassNames
  );

  const indicatorInnerClassNames = 'odin-cursor-pointer';

  return columns.value ? (
    <div className="odin-sticky odin-top-0 odin-z-10 odin-grid odin-min-w-full odin-items-start">
      <div className="odin-flex odin-h-full odin-bg-mono-1">
        {useIndicatorColumn &&
          (headerRowIndicator?.visible === true ? (
            <div
              ref={(element) => element && onIndicatorColumnCreated(element)}
              className={indicatorClassNames}
              onClick={headerRowIndicator.onIndicatorClicked}
            >
              <div className={indicatorInnerClassNames}>{headerRowIndicator.indicator}</div>
            </div>
          ) : (
            <div ref={(element) => element && onIndicatorColumnCreated(element)} className={indicatorClassNames} />
          ))}
        <SignalFilter value={columns} filter={(column) => column.visible}>
          {(filteredColumns) => (
            <DragulaContainer
              containerName="columns"
              items={filteredColumns}
              identityField="id"
              sortDirection="horizontal"
              onItemsChanged={onItemsChanged}
              className="odin-flex odin-w-full odin-border-b odin-border-gray-4"
            >
              {(item: unknown) => {
                const column = item as DataGridColumnSettings;

                return (
                  <DataGridColumn
                    columns={columns}
                    rowWidth={rowWidth}
                    indicatorRowWidth={indicatorRowWidth}
                    availableWidth={availableWidth}
                    key={column.id}
                    column={column}
                    scrollDetection={scrollDetection}
                    search={search}
                    filterSignal={filterSignal}
                    sortSignal={sortSignal}
                    onColumnDragStart={onColumnDragStart}
                    onColumnDragEnd={onColumnDragEnd}
                    forceResetColumnMap={forceResetColumnMap}
                  />
                );
              }}
            </DragulaContainer>
          )}
        </SignalFilter>
      </div>
    </div>
  ) : null;
}
